import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from '../../../components/UI';
import { SharedRecommendedDesign } from '../../../services';

// Minimum horizontal travel (px) before a touch drag counts as a page swipe.
const SWIPE_THRESHOLD = 48;

// #region DesignPreviewModalProps
export interface DesignPreviewModalProps {
    // The wall's recommended designs — the modal pages left/right through these.
    designs: SharedRecommendedDesign[];
    // Which design opened the modal (null = closed).
    initialDesignId: number | null;
    open: boolean;
    // The wall's current local pick(s) — at most one.
    picks: number[];
    locked: boolean;
    onToggle: (designId: number) => void;
    onClose: () => void;
    // Silent refetch used to poll for a still-generating mockup (optional).
    onRefresh?: () => void;
}

// #region DesignPreviewModal
// Full-screen sheet on phones, two-pane dialog from `sm` up: the design on one
// side, its AI mockup (design composited onto the wall photo) on the other.
// Arrows, keyboard and horizontal swipe all page through the wall's designs.
export const DesignPreviewModal: React.FunctionComponent<DesignPreviewModalProps> = (props: DesignPreviewModalProps): JSX.Element | null => {
    const { designs, initialDesignId, open, picks, locked, onToggle, onClose, onRefresh } = props;

    const [index, setIndex] = useState(0);
    // Tracks whether the current mockup <img> has finished loading, so we show a
    // loader instead of a stale/blank image while the new one decodes.
    const [imgLoaded, setImgLoaded] = useState(false);
    const touchStartX = useRef<number | null>(null);

    // Jump to the design that opened the modal.
    useEffect(() => {
        if (!open || initialDesignId === null) return;
        const found = designs.findIndex((d) => d.design_id === initialDesignId);
        setIndex(found >= 0 ? found : 0);
    }, [open, initialDesignId]);

    const canNavigate = designs.length > 1;
    const current = designs[index] ?? null;

    const showPrev = useCallback(() => {
        setIndex((i) => (i - 1 + designs.length) % designs.length);
    }, [designs.length]);
    const showNext = useCallback(() => {
        setIndex((i) => (i + 1) % designs.length);
    }, [designs.length]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (!canNavigate) return;
            else if (e.key === 'ArrowLeft') showPrev();
            else if (e.key === 'ArrowRight') showNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, canNavigate, showPrev, showNext, onClose]);

    // Lock body scroll behind the sheet.
    useEffect(() => {
        if (!open || typeof document === 'undefined') return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [open]);

    const title = current?.title || 'Untitled';
    const selected = current !== null && picks.includes(current.design_id);
    // The mockup lives on the recommended design itself (generated at share).
    const mockupStatus = current?.mockup_status ?? 'PENDING';
    const mockupUrl = current?.ai_mockup_signed_url ?? null;
    const mockupPending = mockupStatus === 'PENDING';

    // Poll for the mockup to appear while it's still generating.
    useEffect(() => {
        if (!open || !onRefresh || !mockupPending) return;
        const timer = setInterval(() => onRefresh(), 4000);
        return () => clearInterval(timer);
    }, [open, onRefresh, mockupPending]);

    // Clear the loaded flag whenever the shown mockup changes or the modal
    // (re)opens/closes — so a previously-loaded image never flashes and the
    // loader shows until the new image's onLoad fires.
    useEffect(() => {
        setImgLoaded(false);
    }, [open, mockupUrl]);

    // #region handleTouchEnd
    const handleTouchStart = (event: React.TouchEvent) => {
        touchStartX.current = event.touches[0]?.clientX ?? null;
    };
    const handleTouchEnd = (event: React.TouchEvent) => {
        if (!canNavigate || touchStartX.current === null) return;
        const delta = (event.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(delta) < SWIPE_THRESHOLD) return;
        if (delta < 0) showNext();
        else showPrev();
    };

    // #region renderMockupPane
    const renderMockupPane = (): JSX.Element => {
        if (mockupStatus === 'GENERATED' && mockupUrl) {
            return (
                <div className="relative flex h-full w-full items-center justify-center">
                    {!imgLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Spinner show={true} />
                        </div>
                    )}
                    <img
                        key={mockupUrl}
                        src={mockupUrl}
                        alt={`${title} on your wall`}
                        onLoad={() => setImgLoaded(true)}
                        className={`max-h-full w-auto max-w-full object-contain ${imgLoaded ? '' : 'invisible'}`}
                    />
                </div>
            );
        }
        if (mockupStatus === 'FAILED') {
            return (
                <div className="p-8 text-center text-sm text-share-muted">
                    We couldn&apos;t generate a wall preview for this design.
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-share-muted">
                <Spinner show={true} /> Generating your wall preview…
            </div>
        );
    };

    if (!open) return null;

    const arrowClass = 'absolute top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white shadow-lg transition hover:bg-black/65';

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
            <button
                type="button"
                aria-label="Close preview"
                onClick={onClose}
                className="absolute inset-0 cursor-default bg-black/50 backdrop-blur-sm"
            />

            <div
                className="relative z-10 flex h-dvh w-full flex-col bg-share-raised text-share-text shadow-2xl sm:h-auto sm:max-h-[88vh] sm:max-w-5xl sm:rounded-3xl"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {/* Sticky header with close */}
                <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-share-border bg-share-raised px-4 py-3 sm:rounded-t-3xl sm:px-6">
                    <div className="min-w-0">
                        <div className="truncate text-base font-semibold sm:text-lg">{title}</div>
                        {canNavigate && (
                            <div className="text-xs text-share-muted">{index + 1} of {designs.length}</div>
                        )}
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onClose}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-share-muted transition hover:bg-share-surface"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Panes — stacked on phones, side by side from sm */}
                <div className="relative flex-1 overflow-y-auto px-4 py-4 sm:px-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="flex h-[38dvh] items-center justify-center overflow-hidden rounded-2xl bg-share-surface sm:h-[52vh]">
                            {current?.thumbnail_url ? (
                                <img
                                    src={current.thumbnail_url}
                                    alt={title}
                                    className="max-h-full w-auto max-w-full object-contain"
                                />
                            ) : (
                                <div className="text-sm text-share-muted">No preview available</div>
                            )}
                        </div>
                        <div className="flex h-[38dvh] items-center justify-center overflow-hidden rounded-2xl bg-share-surface sm:h-[52vh]">
                            {renderMockupPane()}
                        </div>
                    </div>

                    {canNavigate && (
                        <>
                            <button type="button" aria-label="Previous design" onClick={showPrev} className={`${arrowClass} left-5`}>
                                <ChevronLeftIcon className="h-6 w-6" />
                            </button>
                            <button type="button" aria-label="Next design" onClick={showNext} className={`${arrowClass} right-5`}>
                                <ChevronRightIcon className="h-6 w-6" />
                            </button>
                        </>
                    )}
                </div>

                {/* Sticky action bar */}
                <div
                    className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-share-border bg-share-raised px-4 py-3 sm:rounded-b-3xl sm:px-6"
                    style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
                >
                    <button
                        type="button"
                        onClick={onClose}
                        className="min-h-[44px] rounded-xl px-4 text-sm font-semibold text-share-muted transition hover:bg-share-surface"
                    >
                        Close
                    </button>
                    {current && !locked && (
                        <button
                            type="button"
                            onClick={() => onToggle(current.design_id)}
                            style={{ backgroundColor: 'var(--share-primary)', color: 'var(--share-primary-contrast)' }}
                            className="min-h-[44px] rounded-xl px-5 text-sm font-semibold shadow-sm transition hover:brightness-110"
                        >
                            {selected ? 'Deselect' : 'Select this design'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
